import { GitHubConnect } from '@web/components/GitHubConnect'
import { McpConnections } from '@web/components/McpConnections'
import { useIdentity } from '@web/state/identity'
import { useCallback, useEffect, useState } from 'react'

type Connection = {
  connected: boolean
  login?: string
  oauth_configured?: boolean
  auth_method?: string
}

export function GitHubConnections({ userId }: { userId?: string }) {
  const identity = useIdentity()
  const currentUser = identity.state?.current_user?.id || ''
  const target = userId || currentUser
  const [connection, setConnection] = useState<Connection | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const call = useCallback(
    async (method = 'GET') => {
      setBusy(true)
      setError('')
      try {
        const response = await fetch(
          `/v1/connections/${encodeURIComponent(target)}/github`,
          { method, credentials: 'same-origin' },
        )
        const result = await response.json().catch(() => ({}))
        if (!response.ok)
          throw new Error(
            typeof result.detail === 'string'
              ? result.detail
              : `Your GitHub connection could not be loaded (HTTP ${response.status}). Please try again.`,
          )
        if (method === 'DELETE') {
          setConnection((previous) => ({
            ...previous,
            connected: false,
            login: undefined,
          }))
        } else setConnection(result as Connection)
      } catch (failure) {
        setError((failure as Error).message)
      } finally {
        setBusy(false)
      }
    },
    [target],
  )
  useEffect(() => {
    setConnection(null)
    if (target) void call()
  }, [target, call])
  const callbackError =
    new URLSearchParams(window.location.search).get('github') === 'error'
  return (
    <section className="card m-5 max-w-xl p-6">
      <h1 className="text-xl font-semibold">Profile connections</h1>
      <h2 className="mt-5 font-semibold">GitHub</h2>
      {connection ? (
        <p className="my-3">
          {connection.connected
            ? `Connected as ${connection.login}`
            : 'Not connected'}
        </p>
      ) : (
        <p role="status" className="my-3">
          Loading GitHub connection…
        </p>
      )}
      {target === currentUser ? (
        <>
          <p className="my-3 text-sm text-muted">
            Continue to GitHub to authorize access to repositories available to
            you and the Canopy app.
          </p>
          {connection && !connection.oauth_configured && (
            <p role="status" className="my-3 text-sm text-muted">
              GitHub OAuth is not configured for this environment. Contact your
              Canopy administrator.
            </p>
          )}
          <GitHubConnect
            disabled={busy || !connection?.oauth_configured}
            reconnect={connection?.connected}
          />
        </>
      ) : (
        <p className="my-3 text-sm text-muted">
          Only this account’s owner can authorize a GitHub connection.
        </p>
      )}
      {connection?.connected && (
        <button
          type="button"
          className="btn ml-3"
          disabled={busy}
          onClick={() => void call('DELETE')}
        >
          Disconnect GitHub
        </button>
      )}
      {(error || callbackError) && (
        <p role="alert" className="mt-3 text-danger">
          {error ||
            'GitHub authorization was not completed. Start a new connection attempt.'}
        </p>
      )}
      {target === currentUser && <McpConnections />}
      <a className="btn mt-5" href="/">
        Back to agents
      </a>
    </section>
  )
}
